import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useErrorToast } from '~/common/errors/useErrorToast';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { api } from '~/redux/api/createApi';

import { EsignatureTokenStatusValue } from '../types/esignature';

/**
 * Checks if the current user has the required e-signature account token, if not, sends them to login, otherwise will call the intended action (ie: Send to E-signature)
 */
export const useHandleEsignAuthenticateAction = () => {
  const intl = useIntl();
  const { toast } = useToast();
  const { toast: errorToast } = useErrorToast();
  const {
    data: esignatureTokenStatus,
    isLoading: isLoadingEsignatureTokenStatus,
  } = api.endpoints.getTokenStatus.useQuery({});
  const {
    data: authenticateData,
    isLoading: isLoadingAuthenticate,
    error: authenticateError,
  } = api.endpoints.getAuthenticate.useQuery(
    { return_to: window.location.href },
    {
      skip:
        !esignatureTokenStatus ||
        esignatureTokenStatus.accountToken ===
          EsignatureTokenStatusValue.Active,
    },
  );

  const handleEsignAuthenticateAction = useCallback(
    (actionCallback: () => void) => {
      if (!esignatureTokenStatus) {
        toast({
          message: intl.formatMessage({
            id: 'useHandleEsignAuthenticateAction.noEsignatureTokenStatus',
            defaultMessage:
              'This account has not set up an e-signature provider yet.',
            description:
              'Error message when no e-signature token status is found',
          }),
          status: 'danger',
          autoDismiss: false,
        });
        return;
      }

      if (
        esignatureTokenStatus.accountToken !== EsignatureTokenStatusValue.Active
      ) {
        if (authenticateError || !authenticateData) {
          errorToast(
            (authenticateError as Error) ??
              new Error(
                intl.formatMessage({
                  id: 'useHandleEsignAuthenticateAction.noAuthenticateData',
                  defaultMessage:
                    'There was a problem retrieving the e-signature authentication URL.',
                  description:
                    'Error message when there is an error retrieving the e-signature authentication URL.',
                }),
              ),
          );
          return;
        }

        if (authenticateData.authenticateUrl) {
          window.location.href = authenticateData.authenticateUrl;
        }

        return;
      }

      actionCallback();
    },
    [esignatureTokenStatus, authenticateError, authenticateData],
  );

  return {
    handleEsignAuthenticateAction,
    isLoading: isLoadingAuthenticate || isLoadingEsignatureTokenStatus,
  };
};
